import {
  applyServiceWorkerUpdate,
  clearServiceWorkerUpdatePending,
  isServiceWorkerUpdatePending,
  markServiceWorkerUpdatePending,
} from './applyServiceWorkerUpdate';
import { isNewVersionModalOpen, showNewVersionModal } from './showNewVersionModal';

/** Same window as former OfflinePlugin: auto-apply updates discovered at boot. */
export const SW_AUTO_APPLY_WINDOW_MS = 7000;

type HandleServiceWorkerNeedRefreshOptions = {
  updateSW: (reloadPage?: boolean) => Promise<void> | void;
  loadTime: number;
  now?: number;
  registration?: ServiceWorkerRegistration | null;
  /**
   * When true (waiting worker already present on this page load), skip the
   * modal and activate immediately so a refresh-without-accept cannot stick.
   */
  forceApply?: boolean;
  applyUpdate?: typeof applyServiceWorkerUpdate;
  showModal?: typeof showNewVersionModal;
};

/**
 * Decides whether to auto-apply a waiting SW update or prompt the user.
 *
 * - Boot / refresh with a waiting worker (`forceApply`) → activate, no modal.
 * - Update discovered within {@link SW_AUTO_APPLY_WINDOW_MS} of load → auto-apply.
 * - Mid-session update → modal; declining marks pending so the prompt returns
 *   on the next visibility change while the worker is still waiting.
 */
export const handleServiceWorkerNeedRefresh = ({
  updateSW,
  loadTime,
  now = Date.now(),
  registration = null,
  forceApply = false,
  applyUpdate = applyServiceWorkerUpdate,
  showModal = showNewVersionModal,
}: HandleServiceWorkerNeedRefreshOptions): void => {
  const withinAutoApplyWindow = now - loadTime <= SW_AUTO_APPLY_WINDOW_MS;

  if (forceApply || withinAutoApplyWindow) {
    clearServiceWorkerUpdatePending();
    void applyUpdate({ updateSW, registration });
    return;
  }

  showModal(
    () => {
      clearServiceWorkerUpdatePending();
      void applyUpdate({ updateSW, registration });
    },
    () => {
      markServiceWorkerUpdatePending();
    },
  );
};

type MaybeRepromptServiceWorkerUpdateOptions = {
  hasWaitingWorker: boolean;
  updateSW: (reloadPage?: boolean) => Promise<void> | void;
  registration?: ServiceWorkerRegistration | null;
  applyUpdate?: typeof applyServiceWorkerUpdate;
  showModal?: typeof showNewVersionModal;
  isPending?: () => boolean;
  isModalOpen?: () => boolean;
};

/**
 * After the user dismisses the update modal, re-show it when the tab becomes
 * visible again if a waiting worker is still present.
 * Returns true when a prompt/apply was triggered.
 */
export const maybeRepromptServiceWorkerUpdate = ({
  hasWaitingWorker,
  updateSW,
  registration = null,
  applyUpdate = applyServiceWorkerUpdate,
  showModal = showNewVersionModal,
  isPending = isServiceWorkerUpdatePending,
  isModalOpen = isNewVersionModalOpen,
}: MaybeRepromptServiceWorkerUpdateOptions): boolean => {
  if (!hasWaitingWorker) {
    clearServiceWorkerUpdatePending();
    return false;
  }

  if (!isPending() || isModalOpen()) {
    return false;
  }

  // Still outdated after a dismiss — ask again (mid-session, not boot).
  showModal(
    () => {
      clearServiceWorkerUpdatePending();
      void applyUpdate({ updateSW, registration });
    },
    () => {
      markServiceWorkerUpdatePending();
    },
  );
  return true;
};
